// src/pages/Ads.js

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import axios from "axios";


/* =========================================================
   AZURE FUNCTION URLS

   Replace the code values below with the deployed Function
   keys from the OPPIES Function App.
========================================================= */

const GET_ADS_URL =
    "https://oppiesfunc.azurewebsites.net/api/getAds?code=s1i405_KqAigY8LwexxXnsu-Vft3ffMwGR4Mwcz0QIPsAzFusetXwA==";

const CREATE_AD_URL =
    "https://oppiesfunc.azurewebsites.net/api/createAd?code=nhauJwaVPze42gMR-i_-rE3JBpWb3rAgtGkmSqafJYtdAzFuaA8dMw==";

const UPDATE_AD_URL =
    "https://oppiesfunc.azurewebsites.net/api/updateAd?code=hruGAzejFLvQ9jEo_C0xDCGydElmFmlDRw3ZbuvvK3LAAzFuhiW8Bg==";

const DELETE_AD_URL =
    "https://oppiesfunc.azurewebsites.net/api/deleteAd?code=Kq6sI53wVeaPFIDhm3IAcUNUCe6qaGWBJJST5eGiwUmxAzFutyiffA==";

const UPLOAD_AD_IMAGE_URL =
    "https://oppiesfunc.azurewebsites.net/api/uploadAdImage?code=LgBG0JOu0t5SQxhHvrLBZQr4VwKQNAjHNl0i_mpxYlsfAzFu3iPaXw==";


/* =========================================================
   CONSTANTS
========================================================= */

const HOME_SLOTS = [
    4,
    10,
    18,
    25,
];

const MAX_IMAGE_SIZE =
    5 * 1024 * 1024;

const ALLOWED_IMAGE_TYPES = [
    "image/jpeg",
    "image/png",
    "image/webp",
];


const EMPTY_FORM = {
    id: "",

    placement: "home",

    slot: 4,

    advertiserName: "",

    campaignName: "",

    title: "",

    description: "",

    imageUrl: "",

    linkUrl: "",

    alt: "",

    active: true,

    startAt: "",

    endAt: "",

    priority: 10,

    // Geographic targeting
    targetingMode: "countries",

    countries: [
        "NZ",
    ],
};


/* =========================================================
   HELPERS
========================================================= */

function safeNumber(
    value,
    fallback = 0
) {
    const number =
        Number(value);

    return Number.isFinite(number)
        ? number
        : fallback;
}


function formatNumber(
    value
) {
    return safeNumber(value).toLocaleString();
}


function formatCtr(
    value
) {
    const number =
        safeNumber(value);

    return `${number.toFixed(2)}%`;
}


function formatDate(
    value
) {
    if (!value) {
        return "—";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "—";
    }

    return date.toLocaleDateString(
        "en-NZ",
        {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }
    );
}


function toDateTimeLocal(
    value
) {
    if (!value) {
        return "";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "";
    }

    const pad =
        (number) =>
            String(number)
                .padStart(
                    2,
                    "0"
                );

    return (
        `${date.getFullYear()}-` +
        `${pad(date.getMonth() + 1)}-` +
        `${pad(date.getDate())}T` +
        `${pad(date.getHours())}:` +
        `${pad(date.getMinutes())}`
    );
}


function localInputToIso(
    value
) {
    if (!value) {
        return null;
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return null;
    }

    return date.toISOString();
}


function getStatusLabel(
    status
) {
    switch (
        String(status || "")
            .toLowerCase()
    ) {
        case "active":
            return "ACTIVE";

        case "scheduled":
            return "SCHEDULED";

        case "inactive":
            return "PAUSED";

        case "expired":
            return "EXPIRED";

        default:
            return "UNKNOWN";
    }
}


function getStatusStyle(
    status
) {
    switch (
        String(status || "")
            .toLowerCase()
    ) {
        case "active":
            return {
                background:
                    "#e6f4ea",

                color:
                    "#1f6b3a",

                border:
                    "1px solid #b9dec5",
            };

        case "scheduled":
            return {
                background:
                    "#edf3ff",

                color:
                    "#315b9b",

                border:
                    "1px solid #cbd9f2",
            };

        case "inactive":
            return {
                background:
                    "#f3f1ed",

                color:
                    "#665f55",

                border:
                    "1px solid #ddd8cf",
            };

        case "expired":
            return {
                background:
                    "#fbeaea",

                color:
                    "#8f3636",

                border:
                    "1px solid #efcaca",
            };

        default:
            return {
                background:
                    "#f3f3f3",

                color:
                    "#555",

                border:
                    "1px solid #ddd",
            };
    }
}

/* =========================================================
   COUNTRY TARGETING
========================================================= */

const COUNTRY_OPTIONS = [
    {
        code: "NZ",
        label: "New Zealand",
        flag: "🇳🇿",
    },
    {
        code: "AU",
        label: "Australia",
        flag: "🇦🇺",
    },
];


function normaliseTargeting(
    targeting
) {
    if (
        !targeting ||
        typeof targeting !== "object"
    ) {
        return {
            mode: "global",
            countries: [],
        };
    }


    const mode =
        String(
            targeting.mode || ""
        )
            .trim()
            .toLowerCase();


    if (
        mode === "countries" &&
        Array.isArray(
            targeting.countries
        )
    ) {
        const countries =
            [
                ...new Set(
                    targeting.countries
                        .map(
                            (country) =>
                                String(country)
                                    .trim()
                                    .toUpperCase()
                        )
                        .filter(
                            (country) =>
                                /^[A-Z]{2}$/.test(
                                    country
                                )
                        )
                ),
            ];


        if (
            countries.length
        ) {
            return {
                mode: "countries",
                countries,
            };
        }
    }


    return {
        mode: "global",
        countries: [],
    };
}


function getTargetMarketLabel(
    targeting
) {
    const normalized =
        normaliseTargeting(
            targeting
        );


    if (
        normalized.mode ===
        "global"
    ) {
        return "🌏 Global";
    }


    const countries =
        normalized.countries;


    if (
        countries.length === 1
    ) {
        switch (
        countries[0]
        ) {
            case "NZ":
                return "🇳🇿 New Zealand";

            case "AU":
                return "🇦🇺 Australia";

            default:
                return countries[0];
        }
    }


    if (
        countries.includes("NZ") &&
        countries.includes("AU") &&
        countries.length === 2
    ) {
        return "🇳🇿 NZ + 🇦🇺 Australia";
    }


    return countries.join(
        ", "
    );
}

function fileToBase64(
    file
) {
    return new Promise(
        (
            resolve,
            reject
        ) => {
            const reader =
                new FileReader();

            reader.onload =
                () =>
                    resolve(
                        reader.result
                    );

            reader.onerror =
                () =>
                    reject(
                        new Error(
                            "Unable to read the selected image."
                        )
                    );

            reader.readAsDataURL(
                file
            );
        }
    );
}


/* =========================================================
   COMPONENT
========================================================= */

export default function Ads() {

    /* =====================================================
       STATE
    ===================================================== */

    const [
        ads,
        setAds,
    ] =
        useState([]);


    const [
        summary,
        setSummary,
    ] =
        useState({
            total: 0,
            active: 0,
            scheduled: 0,
            inactive: 0,
            expired: 0,
            impressions: 0,
            clicks: 0,
            ctr: 0,
        });


    const [
        loading,
        setLoading,
    ] =
        useState(true);


    const [
        refreshing,
        setRefreshing,
    ] =
        useState(false);


    const [
        error,
        setError,
    ] =
        useState("");


    const [
        successMessage,
        setSuccessMessage,
    ] =
        useState("");


    const [
        modalOpen,
        setModalOpen,
    ] =
        useState(false);


    const [
        modalMode,
        setModalMode,
    ] =
        useState("create");


    const [
        form,
        setForm,
    ] =
        useState({
            ...EMPTY_FORM,
        });


    const [
        selectedFile,
        setSelectedFile,
    ] =
        useState(null);


    const [
        previewUrl,
        setPreviewUrl,
    ] =
        useState("");


    const [
        saving,
        setSaving,
    ] =
        useState(false);


    const [
        uploadProgressText,
        setUploadProgressText,
    ] =
        useState("");


    const [
        actionId,
        setActionId,
    ] =
        useState("");


    const fileInputRef =
        useRef(null);


    /* =====================================================
       LOAD ADS
    ===================================================== */

    const loadAds =
        useCallback(
            async (
                isRefresh = false
            ) => {

                if (isRefresh) {
                    setRefreshing(true);
                } else {
                    setLoading(true);
                }

                setError("");

                try {

                    const response =
                        await axios.get(
                            GET_ADS_URL
                        );


                    const data =
                        response?.data ||
                        {};


                    const nextAds =
                        Array.isArray(
                            data.ads
                        )
                            ? data.ads
                            : [];


                    setAds(
                        nextAds
                    );


                    /* =====================================================
                       BUILD ADMIN SUMMARY FROM RETURNED CAMPAIGNS
                    ===================================================== */

                    const nextSummary =
                        nextAds.reduce(
                            (
                                totals,
                                ad
                            ) => {

                                const status =
                                    String(
                                        ad.campaignStatus ||
                                        ad.status ||
                                        ""
                                    )
                                        .trim()
                                        .toLowerCase();


                                totals.total += 1;


                                if (
                                    status === "active"
                                ) {
                                    totals.active += 1;
                                }


                                if (
                                    status === "scheduled"
                                ) {
                                    totals.scheduled += 1;
                                }


                                if (
                                    status === "inactive"
                                ) {
                                    totals.inactive += 1;
                                }


                                if (
                                    status === "expired"
                                ) {
                                    totals.expired += 1;
                                }


                                totals.impressions +=
                                    safeNumber(
                                        ad.impressionCount
                                    );


                                totals.clicks +=
                                    safeNumber(
                                        ad.clickCount
                                    );


                                return totals;
                            },
                            {
                                total: 0,
                                active: 0,
                                scheduled: 0,
                                inactive: 0,
                                expired: 0,
                                impressions: 0,
                                clicks: 0,
                            }
                        );


                    nextSummary.ctr =
                        nextSummary.impressions > 0
                            ? (
                                nextSummary.clicks /
                                nextSummary.impressions
                            ) * 100
                            : 0;


                    setSummary(
                        nextSummary
                    );


                } catch (
                    loadError
                ) {

                    console.error(
                        "[Ads] Failed to load:",
                        loadError
                    );


                    setError(
                        loadError?.response?.data?.error ||
                        "Unable to load advertising campaigns."
                    );

                } finally {

                    setLoading(false);

                    setRefreshing(false);
                }
            },
            []
        );


    useEffect(
        () => {
            loadAds();
        },
        [
            loadAds,
        ]
    );


    /* =====================================================
       CLEAN PREVIEW OBJECT URL
    ===================================================== */

    useEffect(
        () => {

            return () => {

                if (
                    previewUrl &&
                    previewUrl.startsWith(
                        "blob:"
                    )
                ) {
                    URL.revokeObjectURL(
                        previewUrl
                    );
                }
            };
        },
        [
            previewUrl,
        ]
    );


    /* =====================================================
       FILTER / SORT DISPLAY
    ===================================================== */

    const sortedAds =
        useMemo(
            () => {

                return [
                    ...ads,
                ].sort(
                    (
                        a,
                        b
                    ) => {

                        const slotA =
                            safeNumber(
                                a.slot,
                                9999
                            );

                        const slotB =
                            safeNumber(
                                b.slot,
                                9999
                            );


                        if (
                            slotA !==
                            slotB
                        ) {
                            return (
                                slotA -
                                slotB
                            );
                        }


                        return (
                            new Date(
                                b.createdAt ||
                                0
                            ).getTime() -
                            new Date(
                                a.createdAt ||
                                0
                            ).getTime()
                        );
                    }
                );
            },
            [
                ads,
            ]
        );


    /* =====================================================
       MODAL
    ===================================================== */

    const resetImageState =
        () => {

            if (
                previewUrl &&
                previewUrl.startsWith(
                    "blob:"
                )
            ) {
                URL.revokeObjectURL(
                    previewUrl
                );
            }

            setSelectedFile(
                null
            );

            setPreviewUrl(
                ""
            );

            if (
                fileInputRef.current
            ) {
                fileInputRef.current.value =
                    "";
            }
        };


    const openCreateModal =
        () => {

            resetImageState();

            setForm({
                ...EMPTY_FORM,
            });

            setModalMode(
                "create"
            );

            setUploadProgressText(
                ""
            );

            setError(
                ""
            );

            setModalOpen(
                true
            );
        };


    const openEditModal =
        (
            ad
        ) => {

            resetImageState();

            setForm({
                id:
                    ad.id ||
                    "",

                placement:
                    ad.placement ||
                    "home",

                slot:
                    safeNumber(
                        ad.slot,
                        4
                    ),

                advertiserName:
                    ad.advertiserName ||
                    "",

                campaignName:
                    ad.campaignName ||
                    "",

                title:
                    ad.title ||
                    "",

                description:
                    ad.description ||
                    "",

                imageUrl:
                    ad.imageUrl ||
                    "",

                linkUrl:
                    ad.linkUrl ||
                    "",

                alt:
                    ad.alt ||
                    "",

                active:
                    ad.active ===
                    true,

                startAt:
                    toDateTimeLocal(
                        ad.startAt
                    ),

                endAt:
                    toDateTimeLocal(
                        ad.endAt
                    ),

                priority:
                    safeNumber(
                        ad.priority,
                        10
                    ),

                targetingMode:
                    normaliseTargeting(
                        ad.targeting
                    ).mode,

                countries:
                    normaliseTargeting(
                        ad.targeting
                    ).countries,
            });


            setPreviewUrl(
                ad.imageUrl ||
                ""
            );


            setModalMode(
                "edit"
            );


            setUploadProgressText(
                ""
            );


            setError(
                ""
            );


            setModalOpen(
                true
            );
        };


    const closeModal =
        () => {

            if (
                saving
            ) {
                return;
            }

            resetImageState();

            setModalOpen(
                false
            );

            setUploadProgressText(
                ""
            );
        };


    /* =====================================================
       FORM
    ===================================================== */

    const updateField =
        (
            field,
            value
        ) => {

            setForm(
                (
                    current
                ) => ({
                    ...current,

                    [field]:
                        value,
                })
            );
        };

    const toggleCountry =
        (
            countryCode
        ) => {

            setForm(
                (
                    current
                ) => {

                    const existing =
                        Array.isArray(
                            current.countries
                        )
                            ? current.countries
                            : [];


                    const alreadySelected =
                        existing.includes(
                            countryCode
                        );


                    const countries =
                        alreadySelected
                            ? existing.filter(
                                (country) =>
                                    country !==
                                    countryCode
                            )
                            : [
                                ...existing,
                                countryCode,
                            ];


                    return {
                        ...current,
                        countries,
                    };
                }
            );
        };



    /* =====================================================
       IMAGE
    ===================================================== */

    const handleImageChange =
        (
            event
        ) => {

            const file =
                event.target.files?.[0];


            if (
                !file
            ) {
                return;
            }


            setError(
                ""
            );


            if (
                !ALLOWED_IMAGE_TYPES.includes(
                    file.type
                )
            ) {

                setError(
                    "Please select a JPG, PNG or WebP image."
                );

                event.target.value =
                    "";

                return;
            }


            if (
                file.size >
                MAX_IMAGE_SIZE
            ) {

                setError(
                    "The banner image must be 5 MB or smaller."
                );

                event.target.value =
                    "";

                return;
            }


            if (
                previewUrl &&
                previewUrl.startsWith(
                    "blob:"
                )
            ) {
                URL.revokeObjectURL(
                    previewUrl
                );
            }


            const objectUrl =
                URL.createObjectURL(
                    file
                );


            setSelectedFile(
                file
            );


            setPreviewUrl(
                objectUrl
            );
        };


    const uploadSelectedImage =
        async () => {

            if (
                !selectedFile
            ) {
                return form.imageUrl;
            }


            setUploadProgressText(
                "Uploading banner artwork..."
            );


            const base64 =
                await fileToBase64(
                    selectedFile
                );


            const response =
                await axios.post(
                    UPLOAD_AD_IMAGE_URL,
                    {
                        fileName:
                            selectedFile.name,

                        contentType:
                            selectedFile.type,

                        base64,
                    }
                );


            const imageUrl =
                response?.data?.imageUrl;


            if (
                !imageUrl
            ) {
                throw new Error(
                    "The image uploaded, but no image URL was returned."
                );
            }


            return imageUrl;
        };


    /* =====================================================
       VALIDATION
    ===================================================== */

    const validateForm =
        () => {

            if (
                !form.advertiserName.trim()
            ) {
                return "Advertiser name is required.";
            }


            if (
                !form.campaignName.trim()
            ) {
                return "Campaign name is required.";
            }


            if (
                !HOME_SLOTS.includes(
                    Number(
                        form.slot
                    )
                )
            ) {
                return "Please select a valid Home Feed slot.";
            }

            /* =====================================================
   TARGET MARKET
===================================================== */

            if (
                form.targetingMode !== "global" &&
                form.targetingMode !== "countries"
            ) {
                return "Please select a valid target market.";
            }


            if (
                form.targetingMode === "countries" &&
                (
                    !Array.isArray(
                        form.countries
                    ) ||
                    form.countries.length === 0
                )
            ) {
                return "Please select at least one target country.";
            }

            if (
                !selectedFile &&
                !form.imageUrl
            ) {
                return "Please upload banner artwork.";
            }


            if (
                !form.linkUrl.trim()
            ) {
                return "Destination URL is required.";
            }


            try {

                const parsed =
                    new URL(
                        form.linkUrl.trim()
                    );


                if (
                    parsed.protocol !==
                        "https:" &&
                    parsed.protocol !==
                        "http:"
                ) {
                    return "Destination URL must begin with http:// or https://.";
                }

            } catch {

                return "Please enter a valid destination URL.";
            }


            const priority =
                Number(
                    form.priority
                );


            if (
                !Number.isFinite(
                    priority
                ) ||
                priority < 0 ||
                priority > 100
            ) {
                return "Priority must be between 0 and 100.";
            }


            if (
                form.startAt &&
                form.endAt
            ) {

                const start =
                    new Date(
                        form.startAt
                    );

                const end =
                    new Date(
                        form.endAt
                    );


                if (
                    end <=
                    start
                ) {
                    return "End date must be later than the start date.";
                }
            }


            return "";
        };


    /* =====================================================
       SAVE
    ===================================================== */

    const handleSave =
        async (
            event
        ) => {

            event.preventDefault();


            const validationError =
                validateForm();


            if (
                validationError
            ) {
                setError(
                    validationError
                );

                return;
            }


            setSaving(
                true
            );

            setError(
                ""
            );

            setSuccessMessage(
                ""
            );


            try {

                const imageUrl =
                    await uploadSelectedImage();


                setUploadProgressText(
                    modalMode ===
                        "create"
                        ? "Creating campaign..."
                        : "Updating campaign..."
                );


                const payload = {
                    placement:
                        "home",

                    slot:
                        Number(
                            form.slot
                        ),

                    advertiserName:
                        form.advertiserName.trim(),

                    campaignName:
                        form.campaignName.trim(),

                    title:
                        form.title.trim(),

                    description:
                        form.description.trim(),

                    imageUrl,

                    linkUrl:
                        form.linkUrl.trim(),

                    alt:
                        form.alt.trim(),

                    active:
                        form.active ===
                        true,

                    startAt:
                        localInputToIso(
                            form.startAt
                        ),

                    endAt:
                        localInputToIso(
                            form.endAt
                        ),

                    priority:
                        Number(
                            form.priority
                        ),


                    /* ===============================================
                       GEOGRAPHIC TARGETING
                    =============================================== */

                    targeting:
                        form.targetingMode ===
                            "global"
                            ? {
                                mode:
                                    "global",

                                countries:
                                    [],
                            }
                            : {
                                mode:
                                    "countries",

                                countries:
                                    [
                                        ...form.countries,
                                    ],
                            },


                    updatedBy:
                        "OPPIES Admin",
                };


                if (
                    modalMode ===
                    "create"
                ) {

                    delete payload.updatedBy;

                    payload.createdBy =
                        "OPPIES Admin";


                    await axios.post(
                        CREATE_AD_URL,
                        payload
                    );


                    setSuccessMessage(
                        "Advertisement created successfully."
                    );


                } else {

                    payload.id =
                        form.id;


                    await axios.patch(
                        UPDATE_AD_URL,
                        payload
                    );


                    setSuccessMessage(
                        "Advertisement updated successfully."
                    );
                }


                resetImageState();

                setModalOpen(
                    false
                );


                await loadAds(
                    true
                );


            } catch (
                saveError
            ) {

                console.error(
                    "[Ads] Save failed:",
                    saveError
                );


                setError(
                    saveError?.response?.data?.error ||
                    saveError?.message ||
                    "Unable to save the advertisement."
                );


            } finally {

                setSaving(
                    false
                );

                setUploadProgressText(
                    ""
                );
            }
        };


    /* =====================================================
       PAUSE / ACTIVATE
    ===================================================== */

    const handleToggleActive =
        async (
            ad
        ) => {

            const nextActive =
                !ad.active;


            const action =
                nextActive
                    ? "activate"
                    : "pause";


            if (
                !window.confirm(
                    `Are you sure you want to ${action} "${ad.campaignName}"?`
                )
            ) {
                return;
            }


            setActionId(
                ad.id
            );

            setError(
                ""
            );

            setSuccessMessage(
                ""
            );


            try {

                await axios.patch(
                    UPDATE_AD_URL,
                    {
                        id:
                            ad.id,

                        placement:
                            ad.placement ||
                            "home",

                        active:
                            nextActive,

                        updatedBy:
                            "OPPIES Admin",
                    }
                );


                setSuccessMessage(
                    nextActive
                        ? "Advertisement activated."
                        : "Advertisement paused."
                );


                await loadAds(
                    true
                );


            } catch (
                toggleError
            ) {

                console.error(
                    "[Ads] Status update failed:",
                    toggleError
                );


                setError(
                    toggleError?.response?.data?.error ||
                    "Unable to update the advertisement status."
                );


            } finally {

                setActionId(
                    ""
                );
            }
        };


    /* =====================================================
       DELETE
    ===================================================== */

    const handleDelete =
        async (
            ad
        ) => {

            const confirmed =
                window.confirm(
                    `Delete "${ad.campaignName}"?\n\n` +
                    `Advertiser: ${ad.advertiserName}\n` +
                    `Home Feed slot: ${ad.slot}\n\n` +
                    "The campaign will be removed from OPPIES. " +
                    "Historical advertising analytics will be retained."
                );


            if (
                !confirmed
            ) {
                return;
            }


            setActionId(
                ad.id
            );

            setError(
                ""
            );

            setSuccessMessage(
                ""
            );


            try {

                await axios.delete(
                    DELETE_AD_URL,
                    {
                        data: {
                            id:
                                ad.id,

                            placement:
                                ad.placement ||
                                "home",
                        },
                    }
                );


                setSuccessMessage(
                    "Advertisement deleted successfully."
                );


                await loadAds(
                    true
                );


            } catch (
                deleteError
            ) {

                console.error(
                    "[Ads] Delete failed:",
                    deleteError
                );


                setError(
                    deleteError?.response?.data?.error ||
                    "Unable to delete the advertisement."
                );


            } finally {

                setActionId(
                    ""
                );
            }
        };


    /* =====================================================
       RENDER
    ===================================================== */

    return (
        <div style={styles.page}>

            <div style={styles.container}>

                {/* =========================================
                    HEADER
                ========================================== */}

                <div style={styles.headerRow}>

                    <div>
                        <h1 style={styles.heading}>
                            Ads
                        </h1>

                        <p style={styles.subheading}>
                            Manage OPPIES advertising campaigns and Home Feed placements.
                        </p>
                    </div>


                    <div style={styles.headerActions}>

                        <button
                            type="button"
                            style={styles.secondaryButton}
                            onClick={() =>
                                loadAds(
                                    true
                                )
                            }
                            disabled={
                                refreshing
                            }
                        >
                            {refreshing
                                ? "Refreshing..."
                                : "Refresh"}
                        </button>


                        <button
                            type="button"
                            style={styles.primaryButton}
                            onClick={
                                openCreateModal
                            }
                        >
                            + Create Ad
                        </button>

                    </div>

                </div>


                {/* =========================================
                    MESSAGES
                ========================================== */}

                {error && (
                    <div style={styles.errorBox}>
                        {error}
                    </div>
                )}


                {successMessage && (
                    <div style={styles.successBox}>
                        {successMessage}
                    </div>
                )}


                {/* =========================================
                    KPI CARDS
                ========================================== */}

                <div style={styles.kpiGrid}>

                    <KpiCard
                        label="Active Campaigns"
                        value={
                            formatNumber(
                                summary.active
                            )
                        }
                        helper={
                            `${formatNumber(summary.scheduled)} scheduled`
                        }
                    />


                    <KpiCard
                        label="Impressions"
                        value={
                            formatNumber(
                                summary.impressions
                            )
                        }
                        helper="Total recorded views"
                    />


                    <KpiCard
                        label="Clicks"
                        value={
                            formatNumber(
                                summary.clicks
                            )
                        }
                        helper="Total advertiser visits"
                    />


                    <KpiCard
                        label="CTR"
                        value={
                            formatCtr(
                                summary.ctr
                            )
                        }
                        helper="Clicks ÷ impressions"
                    />

                </div>


                {/* =========================================
                    CAMPAIGNS
                ========================================== */}

                <div style={styles.sectionCard}>

                    <div style={styles.sectionHeader}>

                        <div>
                            <h2 style={styles.sectionTitle}>
                                Campaigns
                            </h2>

                            <div style={styles.sectionMeta}>
                                {formatNumber(
                                    summary.total
                                )} total campaign
                                {summary.total === 1
                                    ? ""
                                    : "s"}
                            </div>
                        </div>

                    </div>


                    {loading ? (

                        <div style={styles.emptyState}>
                            Loading advertising campaigns...
                        </div>

                    ) : sortedAds.length === 0 ? (

                        <div style={styles.emptyState}>

                            <div style={styles.emptyTitle}>
                                No advertising campaigns
                            </div>

                            <div style={styles.emptyText}>
                                Create the first OPPIES Home Feed advertisement.
                            </div>

                            <button
                                type="button"
                                style={{
                                    ...styles.primaryButton,
                                    marginTop: 18,
                                }}
                                onClick={
                                    openCreateModal
                                }
                            >
                                + Create Ad
                            </button>

                        </div>

                    ) : (

                        <div style={styles.campaignList}>

                            {sortedAds.map(
                                (
                                    ad
                                ) => (

                                    <CampaignCard
                                        key={
                                            ad.id
                                        }
                                        ad={
                                            ad
                                        }
                                        busy={
                                            actionId ===
                                            ad.id
                                        }
                                        onEdit={
                                            openEditModal
                                        }
                                        onToggle={
                                            handleToggleActive
                                        }
                                        onDelete={
                                            handleDelete
                                        }
                                    />

                                )
                            )}

                        </div>

                    )}

                </div>

            </div>


            {/* =============================================
                CREATE / EDIT MODAL
            ============================================== */}

            {modalOpen && (

                <div
                    style={styles.modalBackdrop}
                    onMouseDown={
                        (
                            event
                        ) => {
                            if (
                                event.target ===
                                event.currentTarget
                            ) {
                                closeModal();
                            }
                        }
                    }
                >

                    <div style={styles.modal}>

                        <div style={styles.modalHeader}>

                            <div>
                                <h2 style={styles.modalTitle}>
                                    {modalMode ===
                                    "create"
                                        ? "Create Advertisement"
                                        : "Edit Advertisement"}
                                </h2>

                                <p style={styles.modalSubtitle}>
                                    Home Feed sponsored banner campaign
                                </p>
                            </div>


                            <button
                                type="button"
                                style={styles.closeButton}
                                onClick={
                                    closeModal
                                }
                                disabled={
                                    saving
                                }
                                aria-label="Close"
                            >
                                ×
                            </button>

                        </div>


                        <form
                            onSubmit={
                                handleSave
                            }
                        >

                            <div style={styles.modalBody}>

                                {/* =========================
                                    CAMPAIGN
                                ========================== */}

                                <div style={styles.formSection}>

                                    <h3 style={styles.formSectionTitle}>
                                        Campaign
                                    </h3>


                                    <div style={styles.twoColumnGrid}>

                                        <Field
                                            label="Advertiser Name"
                                            required
                                        >
                                            <input
                                                style={styles.input}
                                                value={
                                                    form.advertiserName
                                                }
                                                onChange={
                                                    (
                                                        event
                                                    ) =>
                                                        updateField(
                                                            "advertiserName",
                                                            event.target.value
                                                        )
                                                }
                                                placeholder="Example Business"
                                                disabled={
                                                    saving
                                                }
                                            />
                                        </Field>


                                        <Field
                                            label="Campaign Name"
                                            required
                                        >
                                            <input
                                                style={styles.input}
                                                value={
                                                    form.campaignName
                                                }
                                                onChange={
                                                    (
                                                        event
                                                    ) =>
                                                        updateField(
                                                            "campaignName",
                                                            event.target.value
                                                        )
                                                }
                                                placeholder="Spring Campaign"
                                                disabled={
                                                    saving
                                                }
                                            />
                                        </Field>

                                    </div>


                                    <div style={styles.twoColumnGrid}>

                                        <Field label="Title">
                                            <input
                                                style={styles.input}
                                                value={
                                                    form.title
                                                }
                                                onChange={
                                                    (
                                                        event
                                                    ) =>
                                                        updateField(
                                                            "title",
                                                            event.target.value
                                                        )
                                                }
                                                placeholder="Optional"
                                                disabled={
                                                    saving
                                                }
                                            />
                                        </Field>


                                        <Field label="Alt Text">
                                            <input
                                                style={styles.input}
                                                value={
                                                    form.alt
                                                }
                                                onChange={
                                                    (
                                                        event
                                                    ) =>
                                                        updateField(
                                                            "alt",
                                                            event.target.value
                                                        )
                                                }
                                                placeholder="Description of banner artwork"
                                                disabled={
                                                    saving
                                                }
                                            />
                                        </Field>

                                    </div>


                                    <Field label="Internal Description">
                                        <textarea
                                            style={styles.textarea}
                                            value={
                                                form.description
                                            }
                                            onChange={
                                                (
                                                    event
                                                ) =>
                                                    updateField(
                                                        "description",
                                                        event.target.value
                                                    )
                                            }
                                            placeholder="Optional campaign notes or description"
                                            disabled={
                                                saving
                                            }
                                        />
                                    </Field>

                                </div>


                                {/* =========================
                                    ARTWORK
                                ========================== */}

                                <div style={styles.formSection}>

                                    <h3 style={styles.formSectionTitle}>
                                        Banner Artwork
                                    </h3>


                                    <div style={styles.artworkBox}>

                                        {previewUrl ? (

                                            <img
                                                src={
                                                    previewUrl
                                                }
                                                alt="Advertisement preview"
                                                style={styles.artworkPreview}
                                            />

                                        ) : (

                                            <div style={styles.artworkPlaceholder}>
                                                Banner preview
                                            </div>

                                        )}


                                        <div style={styles.sponsoredPreview}>
                                            Sponsored
                                        </div>

                                    </div>


                                    <div style={styles.artworkActions}>

                                        <input
                                            ref={
                                                fileInputRef
                                            }
                                            type="file"
                                            accept="image/jpeg,image/png,image/webp"
                                            style={{
                                                display:
                                                    "none",
                                            }}
                                            onChange={
                                                handleImageChange
                                            }
                                        />


                                        <button
                                            type="button"
                                            style={styles.secondaryButton}
                                            onClick={() =>
                                                fileInputRef.current?.click()
                                            }
                                            disabled={
                                                saving
                                            }
                                        >
                                            {previewUrl
                                                ? "Replace Image"
                                                : "Choose Image"}
                                        </button>


                                        {selectedFile && (
                                            <span style={styles.fileName}>
                                                {selectedFile.name}
                                            </span>
                                        )}

                                    </div>


                                    <div style={styles.helpText}>
                                        Recommended artwork: approximately 1200 × 300 px.
                                        JPG, PNG or WebP. Maximum 5 MB. The banner will
                                        occupy the same compact space as an Op Shop card.
                                    </div>

                                </div>


                                {/* =========================
                                    DESTINATION
                                ========================== */}

                                <div style={styles.formSection}>

                                    <h3 style={styles.formSectionTitle}>
                                        Destination
                                    </h3>


                                    <Field
                                        label="Destination URL"
                                        required
                                    >
                                        <input
                                            style={styles.input}
                                            value={
                                                form.linkUrl
                                            }
                                            onChange={
                                                (
                                                    event
                                                ) =>
                                                    updateField(
                                                        "linkUrl",
                                                        event.target.value
                                                    )
                                            }
                                            placeholder="https://advertiser.co.nz"
                                            disabled={
                                                saving
                                            }
                                        />
                                    </Field>


                                    <div style={styles.helpText}>
                                        The entire banner will be clickable. OPPIES records
                                        the click before opening this destination.
                                    </div>

                                </div>

                                {/* =========================
    TARGET MARKET
========================== */}

                                <div style={styles.formSection}>

                                    <h3 style={styles.formSectionTitle}>
                                        Target Market
                                    </h3>


                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns:
                                                "repeat(auto-fit, minmax(180px, 1fr))",
                                            gap: 12,
                                            marginBottom: 14,
                                        }}
                                    >

                                        {/* =============================================
            GLOBAL
        ============================================= */}

                                        <button
                                            type="button"
                                            disabled={saving}
                                            onClick={() => {

                                                updateField(
                                                    "targetingMode",
                                                    "global"
                                                );

                                                updateField(
                                                    "countries",
                                                    []
                                                );
                                            }}
                                            style={{
                                                padding: "16px 18px",
                                                borderRadius: 12,
                                                cursor:
                                                    saving
                                                        ? "not-allowed"
                                                        : "pointer",

                                                textAlign: "left",

                                                background:
                                                    form.targetingMode ===
                                                        "global"
                                                        ? "#f3e5c2"
                                                        : "#fff",

                                                border:
                                                    form.targetingMode ===
                                                        "global"
                                                        ? "2px solid #bfa97c"
                                                        : "1px solid #ddd8cf",

                                                transition:
                                                    "all 0.15s ease",
                                            }}
                                        >

                                            <div
                                                style={{
                                                    fontSize: 22,
                                                    marginBottom: 6,
                                                }}
                                            >
                                                🌏
                                            </div>

                                            <div
                                                style={{
                                                    fontWeight: 700,
                                                    color: "#2f2b25",
                                                }}
                                            >
                                                Global
                                            </div>

                                            <div
                                                style={{
                                                    fontSize: 12,
                                                    color: "#777",
                                                    marginTop: 4,
                                                }}
                                            >
                                                Show this campaign in every OPPIES market.
                                            </div>

                                        </button>


                                        {/* =============================================
            COUNTRY TARGETING
        ============================================= */}

                                        <button
                                            type="button"
                                            disabled={saving}
                                            onClick={() => {

                                                updateField(
                                                    "targetingMode",
                                                    "countries"
                                                );


                                                if (
                                                    !form.countries ||
                                                    form.countries.length === 0
                                                ) {
                                                    updateField(
                                                        "countries",
                                                        ["NZ"]
                                                    );
                                                }
                                            }}
                                            style={{
                                                padding: "16px 18px",
                                                borderRadius: 12,
                                                cursor:
                                                    saving
                                                        ? "not-allowed"
                                                        : "pointer",

                                                textAlign: "left",

                                                background:
                                                    form.targetingMode ===
                                                        "countries"
                                                        ? "#f3e5c2"
                                                        : "#fff",

                                                border:
                                                    form.targetingMode ===
                                                        "countries"
                                                        ? "2px solid #bfa97c"
                                                        : "1px solid #ddd8cf",

                                                transition:
                                                    "all 0.15s ease",
                                            }}
                                        >

                                            <div
                                                style={{
                                                    fontSize: 22,
                                                    marginBottom: 6,
                                                }}
                                            >
                                                📍
                                            </div>

                                            <div
                                                style={{
                                                    fontWeight: 700,
                                                    color: "#2f2b25",
                                                }}
                                            >
                                                Selected Countries
                                            </div>

                                            <div
                                                style={{
                                                    fontSize: 12,
                                                    color: "#777",
                                                    marginTop: 4,
                                                }}
                                            >
                                                Deliver only in the selected OPPIES markets.
                                            </div>

                                        </button>

                                    </div>


                                    {/* =============================================
        COUNTRY SELECTION
    ============================================= */}

                                    {form.targetingMode ===
                                        "countries" && (

                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexWrap: "wrap",
                                                    gap: 10,
                                                    marginTop: 8,
                                                }}
                                            >

                                                {COUNTRY_OPTIONS.map(
                                                    (
                                                        country
                                                    ) => {

                                                        const selected =
                                                            form.countries.includes(
                                                                country.code
                                                            );


                                                        return (

                                                            <button
                                                                key={
                                                                    country.code
                                                                }
                                                                type="button"
                                                                disabled={
                                                                    saving
                                                                }
                                                                onClick={() =>
                                                                    toggleCountry(
                                                                        country.code
                                                                    )
                                                                }
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: 9,

                                                                    padding:
                                                                        "10px 14px",

                                                                    borderRadius:
                                                                        999,

                                                                    cursor:
                                                                        saving
                                                                            ? "not-allowed"
                                                                            : "pointer",

                                                                    background:
                                                                        selected
                                                                            ? "#e8f5e9"
                                                                            : "#fff",

                                                                    color:
                                                                        selected
                                                                            ? "#1f6b3a"
                                                                            : "#555",

                                                                    border:
                                                                        selected
                                                                            ? "1px solid #9fcfaf"
                                                                            : "1px solid #d9d5cc",

                                                                    fontWeight:
                                                                        selected
                                                                            ? 700
                                                                            : 500,
                                                                }}
                                                            >

                                                                <span
                                                                    style={{
                                                                        fontSize: 19,
                                                                    }}
                                                                >
                                                                    {country.flag}
                                                                </span>

                                                                <span>
                                                                    {country.label}
                                                                </span>

                                                                {selected && (
                                                                    <span>
                                                                        ✓
                                                                    </span>
                                                                )}

                                                            </button>
                                                        );
                                                    }
                                                )}

                                            </div>

                                        )}


                                    <div style={styles.helpText}>

                                        {form.targetingMode ===
                                            "global"
                                            ? "This advertisement can be shown to OPPIES users in any country."
                                            : form.countries.length === 2
                                                ? "This advertisement will be shown in New Zealand and Australia."
                                                : form.countries.includes(
                                                    "NZ"
                                                )
                                                    ? "This advertisement will only be shown to users in New Zealand."
                                                    : form.countries.includes(
                                                        "AU"
                                                    )
                                                        ? "This advertisement will only be shown to users in Australia."
                                                        : "Select at least one country."}

                                    </div>

                                </div>


                                {/* =========================
                                    PLACEMENT
                                ========================== */}

                                <div style={styles.formSection}>

                                    <h3 style={styles.formSectionTitle}>
                                        Placement
                                    </h3>


                                    <div style={styles.twoColumnGrid}>

                                        <Field
                                            label="Placement"
                                        >
                                            <select
                                                style={styles.input}
                                                value="home"
                                                disabled
                                            >
                                                <option value="home">
                                                    Home Feed
                                                </option>
                                            </select>
                                        </Field>


                                        <Field
                                            label="Home Feed Slot"
                                            required
                                        >
                                            <select
                                                style={styles.input}
                                                value={
                                                    form.slot
                                                }
                                                onChange={
                                                    (
                                                        event
                                                    ) =>
                                                        updateField(
                                                            "slot",
                                                            Number(
                                                                event.target.value
                                                            )
                                                        )
                                                }
                                                disabled={
                                                    saving
                                                }
                                            >
                                                {HOME_SLOTS.map(
                                                    (
                                                        slot
                                                    ) => (
                                                        <option
                                                            key={
                                                                slot
                                                            }
                                                            value={
                                                                slot
                                                            }
                                                        >
                                                            Position {slot}
                                                        </option>
                                                    )
                                                )}
                                            </select>
                                        </Field>

                                    </div>


                                    <div style={styles.helpText}>
                                        If multiple active campaigns share the same slot,
                                        priority determines which campaign is shown. Campaigns
                                        with equal priority rotate.
                                    </div>

                                </div>


                                {/* =========================
                                    SCHEDULE
                                ========================== */}

                                <div style={styles.formSection}>

                                    <h3 style={styles.formSectionTitle}>
                                        Schedule
                                    </h3>


                                    <div style={styles.twoColumnGrid}>

                                        <Field label="Start Date / Time">
                                            <input
                                                type="datetime-local"
                                                style={styles.input}
                                                value={
                                                    form.startAt
                                                }
                                                onChange={
                                                    (
                                                        event
                                                    ) =>
                                                        updateField(
                                                            "startAt",
                                                            event.target.value
                                                        )
                                                }
                                                disabled={
                                                    saving
                                                }
                                            />
                                        </Field>


                                        <Field label="End Date / Time">
                                            <input
                                                type="datetime-local"
                                                style={styles.input}
                                                value={
                                                    form.endAt
                                                }
                                                onChange={
                                                    (
                                                        event
                                                    ) =>
                                                        updateField(
                                                            "endAt",
                                                            event.target.value
                                                        )
                                                }
                                                disabled={
                                                    saving
                                                }
                                            />
                                        </Field>

                                    </div>


                                    <div style={styles.helpText}>
                                        Leave start blank to begin immediately. Leave end
                                        blank to keep running until the campaign is paused.
                                    </div>

                                </div>


                                {/* =========================
                                    DELIVERY
                                ========================== */}

                                <div style={styles.formSection}>

                                    <h3 style={styles.formSectionTitle}>
                                        Delivery
                                    </h3>


                                    <div style={styles.twoColumnGrid}>

                                        <Field label="Priority">
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                step="1"
                                                style={styles.input}
                                                value={
                                                    form.priority
                                                }
                                                onChange={
                                                    (
                                                        event
                                                    ) =>
                                                        updateField(
                                                            "priority",
                                                            event.target.value
                                                        )
                                                }
                                                disabled={
                                                    saving
                                                }
                                            />
                                        </Field>


                                        <Field label="Campaign Status">

                                            <label style={styles.toggleRow}>

                                                <input
                                                    type="checkbox"
                                                    checked={
                                                        form.active
                                                    }
                                                    onChange={
                                                        (
                                                            event
                                                        ) =>
                                                            updateField(
                                                                "active",
                                                                event.target.checked
                                                            )
                                                    }
                                                    disabled={
                                                        saving
                                                    }
                                                    style={styles.checkbox}
                                                />

                                                <span>
                                                    {form.active
                                                        ? "Active"
                                                        : "Paused"}
                                                </span>

                                            </label>

                                        </Field>

                                    </div>


                                    <div style={styles.helpText}>
                                        Higher priority campaigns take precedence over lower
                                        priority campaigns assigned to the same Home Feed slot.
                                    </div>

                                </div>


                                {uploadProgressText && (
                                    <div style={styles.progressBox}>
                                        {uploadProgressText}
                                    </div>
                                )}

                            </div>


                            {/* =============================
                                MODAL FOOTER
                            ============================== */}

                            <div style={styles.modalFooter}>

                                <button
                                    type="button"
                                    style={styles.secondaryButton}
                                    onClick={
                                        closeModal
                                    }
                                    disabled={
                                        saving
                                    }
                                >
                                    Cancel
                                </button>


                                <button
                                    type="submit"
                                    style={styles.primaryButton}
                                    disabled={
                                        saving
                                    }
                                >
                                    {saving
                                        ? "Saving..."
                                        : modalMode ===
                                            "create"
                                            ? "Create Ad"
                                            : "Save Changes"}
                                </button>

                            </div>

                        </form>

                    </div>

                </div>

            )}

        </div>
    );
}


/* =========================================================
   KPI CARD
========================================================= */

function KpiCard({
    label,
    value,
    helper,
}) {
    return (
        <div style={styles.kpiCard}>

            <div style={styles.kpiLabel}>
                {label}
            </div>

            <div style={styles.kpiValue}>
                {value}
            </div>

            <div style={styles.kpiHelper}>
                {helper}
            </div>

        </div>
    );
}


/* =========================================================
   FIELD
========================================================= */

function Field({
    label,
    required = false,
    children,
}) {
    return (
        <label style={styles.field}>

            <span style={styles.label}>
                {label}

                {required && (
                    <span style={styles.required}>
                        *
                    </span>
                )}
            </span>

            {children}

        </label>
    );
}


/* =========================================================
   CAMPAIGN CARD
========================================================= */

function CampaignCard({
    ad,
    busy,
    onEdit,
    onToggle,
    onDelete,
}) {

    const campaignStatus =
        ad.campaignStatus ||
        ad.status ||
        (
            ad.active
                ? "active"
                : "inactive"
        );


    const statusStyle =
        getStatusStyle(
            campaignStatus
        );


    return (
        <div style={styles.campaignCard}>

            {/* =============================================
                ARTWORK
            ============================================== */}

            <div style={styles.campaignArtwork}>

                {ad.imageUrl ? (

                    <img
                        src={
                            ad.imageUrl
                        }
                        alt={
                            ad.alt ||
                            ad.campaignName ||
                            "Advertisement"
                        }
                        style={styles.campaignImage}
                    />

                ) : (

                    <div style={styles.campaignImageMissing}>
                        No image
                    </div>

                )}


                <div style={styles.smallSponsoredBadge}>
                    Sponsored
                </div>

            </div>


            {/* =============================================
                INFO
            ============================================== */}

            <div style={styles.campaignContent}>

                <div style={styles.campaignTopRow}>

                    <div style={styles.campaignIdentity}>

                        <div style={styles.advertiserName}>
                            {ad.advertiserName ||
                                "Unnamed advertiser"}
                        </div>

                        <div style={styles.campaignName}>
                            {ad.campaignName ||
                                "Unnamed campaign"}
                        </div>

                    </div>


                    <span
                        style={{
                            ...styles.statusPill,
                            ...statusStyle,
                        }}
                    >
                        {getStatusLabel(
                            campaignStatus
                        )}
                    </span>

                </div>


                <div style={styles.metaGrid}>

                    <Meta
                        label="Placement"
                        value="Home Feed"
                    />

                    <Meta
                        label="Market"
                        value={
                            getTargetMarketLabel(
                                ad.targeting
                            )
                        }
                    />

                    <Meta
                        label="Slot"
                        value={
                            `Position ${ad.slot}`
                        }
                    />

                    <Meta
                        label="Start"
                        value={
                            formatDate(
                                ad.startAt
                            )
                        }
                    />

                    <Meta
                        label="End"
                        value={
                            formatDate(
                                ad.endAt
                            )
                        }
                    />

                    <Meta
                        label="Priority"
                        value={
                            safeNumber(
                                ad.priority
                            )
                        }
                    />

                </div>


                <div style={styles.analyticsRow}>

                    <AnalyticsMetric
                        label="Impressions"
                        value={
                            formatNumber(
                                ad.impressionCount
                            )
                        }
                    />

                    <AnalyticsMetric
                        label="Clicks"
                        value={
                            formatNumber(
                                ad.clickCount
                            )
                        }
                    />

                    <AnalyticsMetric
                        label="CTR"
                        value={
                            formatCtr(
                                ad.ctr
                            )
                        }
                    />

                </div>


                <div style={styles.cardActions}>

                    <button
                        type="button"
                        style={styles.smallSecondaryButton}
                        onClick={() =>
                            onEdit(
                                ad
                            )
                        }
                        disabled={
                            busy
                        }
                    >
                        Edit
                    </button>


                    <button
                        type="button"
                        style={
                            ad.active
                                ? styles.pauseButton
                                : styles.activateButton
                        }
                        onClick={() =>
                            onToggle(
                                ad
                            )
                        }
                        disabled={
                            busy
                        }
                    >
                        {busy
                            ? "Working..."
                            : ad.active
                                ? "Pause"
                                : "Activate"}
                    </button>


                    <button
                        type="button"
                        style={styles.deleteButton}
                        onClick={() =>
                            onDelete(
                                ad
                            )
                        }
                        disabled={
                            busy
                        }
                    >
                        Delete
                    </button>

                </div>

            </div>

        </div>
    );
}


/* =========================================================
   META
========================================================= */

function Meta({
    label,
    value,
}) {
    return (
        <div style={styles.metaItem}>

            <div style={styles.metaLabel}>
                {label}
            </div>

            <div style={styles.metaValue}>
                {value}
            </div>

        </div>
    );
}


/* =========================================================
   ANALYTICS
========================================================= */

function AnalyticsMetric({
    label,
    value,
}) {
    return (
        <div style={styles.analyticsMetric}>

            <div style={styles.analyticsValue}>
                {value}
            </div>

            <div style={styles.analyticsLabel}>
                {label}
            </div>

        </div>
    );
}


/* =========================================================
   STYLES
========================================================= */

const styles = {

    page: {
        minHeight:
            "100vh",

        background:
            "#f7f4ee",

        color:
            "#18382c",

        padding:
            "28px 24px 60px",

        boxSizing:
            "border-box",
    },


    container: {
        width:
            "100%",

        maxWidth:
            1050,

        margin:
            "0 auto",
    },


    headerRow: {
        display:
            "flex",

        justifyContent:
            "space-between",

        alignItems:
            "flex-start",

        gap:
            20,

        flexWrap:
            "wrap",

        marginBottom:
            24,
    },


    heading: {
        margin:
            0,

        fontSize:
            34,

        lineHeight:
            1.1,

        fontWeight:
            800,

        color:
            "#18382c",
    },


    subheading: {
        margin:
            "8px 0 0",

        color:
            "#6d746f",

        fontSize:
            15,

        lineHeight:
            1.5,
    },


    headerActions: {
        display:
            "flex",

        gap:
            10,

        alignItems:
            "center",

        flexWrap:
            "wrap",
    },


    primaryButton: {
        appearance:
            "none",

        border:
            "1px solid #18382c",

        background:
            "#18382c",

        color:
            "#ffffff",

        borderRadius:
            10,

        padding:
            "11px 17px",

        fontSize:
            14,

        fontWeight:
            700,

        cursor:
            "pointer",

        lineHeight:
            1.2,
    },


    secondaryButton: {
        appearance:
            "none",

        border:
            "1px solid #d8d4ca",

        background:
            "#ffffff",

        color:
            "#18382c",

        borderRadius:
            10,

        padding:
            "11px 17px",

        fontSize:
            14,

        fontWeight:
            700,

        cursor:
            "pointer",

        lineHeight:
            1.2,
    },


    errorBox: {
        background:
            "#fff1f1",

        border:
            "1px solid #efcccc",

        color:
            "#8a3030",

        borderRadius:
            10,

        padding:
            "12px 14px",

        marginBottom:
            18,

        fontSize:
            14,
    },


    successBox: {
        background:
            "#edf7ef",

        border:
            "1px solid #c8e4cf",

        color:
            "#28613b",

        borderRadius:
            10,

        padding:
            "12px 14px",

        marginBottom:
            18,

        fontSize:
            14,
    },


    kpiGrid: {
        display:
            "grid",

        gridTemplateColumns:
            "repeat(auto-fit, minmax(190px, 1fr))",

        gap:
            14,

        marginBottom:
            22,
    },


    kpiCard: {
        background:
            "#ffffff",

        border:
            "1px solid #e3ded4",

        borderRadius:
            14,

        padding:
            "18px 19px",

        boxShadow:
            "0 3px 12px rgba(20, 50, 38, 0.035)",
    },


    kpiLabel: {
        color:
            "#747a76",

        fontSize:
            12,

        fontWeight:
            800,

        textTransform:
            "uppercase",

        letterSpacing:
            "0.055em",
    },


    kpiValue: {
        color:
            "#18382c",

        fontSize:
            29,

        fontWeight:
            800,

        marginTop:
            7,

        lineHeight:
            1.1,
    },


    kpiHelper: {
        color:
            "#929690",

        fontSize:
            12,

        marginTop:
            6,
    },


    sectionCard: {
        background:
            "#ffffff",

        border:
            "1px solid #e3ded4",

        borderRadius:
            15,

        overflow:
            "hidden",

        boxShadow:
            "0 3px 12px rgba(20, 50, 38, 0.035)",
    },


    sectionHeader: {
        display:
            "flex",

        justifyContent:
            "space-between",

        alignItems:
            "center",

        padding:
            "20px 22px",

        borderBottom:
            "1px solid #ece8e0",
    },


    sectionTitle: {
        margin:
            0,

        fontSize:
            20,

        color:
            "#18382c",
    },


    sectionMeta: {
        marginTop:
            4,

        fontSize:
            12,

        color:
            "#8a8e89",
    },


    campaignList: {
        display:
            "flex",

        flexDirection:
            "column",
    },


    campaignCard: {
        padding:
            20,

        display:
            "grid",

        gridTemplateColumns:
            "minmax(220px, 300px) 1fr",

        gap:
            20,

        borderBottom:
            "1px solid #ece8e0",
    },


    campaignArtwork: {
        position:
            "relative",

        width:
            "100%",

        aspectRatio:
            "4 / 1",

        minHeight:
            75,

        background:
            "#efede7",

        borderRadius:
            10,

        overflow:
            "hidden",

        alignSelf:
            "start",

        border:
            "1px solid #e1ddd4",
    },


    campaignImage: {
        width:
            "100%",

        height:
            "100%",

        objectFit:
            "cover",

        display:
            "block",
    },


    campaignImageMissing: {
        width:
            "100%",

        height:
            "100%",

        minHeight:
            75,

        display:
            "flex",

        alignItems:
            "center",

        justifyContent:
            "center",

        color:
            "#8b8e89",

        fontSize:
            13,
    },


    smallSponsoredBadge: {
        position:
            "absolute",

        right:
            7,

        bottom:
            7,

        background:
            "rgba(20, 35, 29, 0.78)",

        color:
            "#ffffff",

        borderRadius:
            5,

        padding:
            "3px 6px",

        fontSize:
            9,

        fontWeight:
            700,
    },


    campaignContent: {
        minWidth:
            0,
    },


    campaignTopRow: {
        display:
            "flex",

        alignItems:
            "flex-start",

        justifyContent:
            "space-between",

        gap:
            15,
    },


    campaignIdentity: {
        minWidth:
            0,
    },


    advertiserName: {
        fontSize:
            17,

        fontWeight:
            800,

        color:
            "#18382c",

        overflow:
            "hidden",

        textOverflow:
            "ellipsis",
    },


    campaignName: {
        marginTop:
            3,

        color:
            "#737873",

        fontSize:
            13,
    },


    statusPill: {
        borderRadius:
            999,

        padding:
            "5px 9px",

        fontSize:
            10,

        lineHeight:
            1,

        fontWeight:
            800,

        letterSpacing:
            "0.04em",

        whiteSpace:
            "nowrap",
    },


    metaGrid: {
        display:
            "grid",

        gridTemplateColumns:
            "repeat(auto-fit, minmax(90px, 1fr))",

        gap:
            12,

        marginTop:
            17,
    },


    metaItem: {
        minWidth:
            0,
    },


    metaLabel: {
        color:
            "#969994",

        fontSize:
            10,

        fontWeight:
            700,

        textTransform:
            "uppercase",

        letterSpacing:
            "0.04em",
    },


    metaValue: {
        color:
            "#39473f",

        fontSize:
            12,

        fontWeight:
            600,

        marginTop:
            3,
    },


    analyticsRow: {
        display:
            "flex",

        gap:
            10,

        marginTop:
            17,

        flexWrap:
            "wrap",
    },


    analyticsMetric: {
        background:
            "#f8f6f1",

        border:
            "1px solid #ebe6dc",

        borderRadius:
            9,

        padding:
            "8px 12px",

        minWidth:
            90,
    },


    analyticsValue: {
        color:
            "#18382c",

        fontSize:
            15,

        fontWeight:
            800,
    },


    analyticsLabel: {
        color:
            "#8a8e89",

        fontSize:
            10,

        marginTop:
            2,
    },


    cardActions: {
        display:
            "flex",

        alignItems:
            "center",

        gap:
            8,

        flexWrap:
            "wrap",

        marginTop:
            17,
    },


    smallSecondaryButton: {
        border:
            "1px solid #d8d4ca",

        background:
            "#ffffff",

        color:
            "#18382c",

        borderRadius:
            8,

        padding:
            "8px 12px",

        fontSize:
            12,

        fontWeight:
            700,

        cursor:
            "pointer",
    },


    pauseButton: {
        border:
            "1px solid #d6c59d",

        background:
            "#fff8e8",

        color:
            "#735c22",

        borderRadius:
            8,

        padding:
            "8px 12px",

        fontSize:
            12,

        fontWeight:
            700,

        cursor:
            "pointer",
    },


    activateButton: {
        border:
            "1px solid #bcdac5",

        background:
            "#edf7ef",

        color:
            "#28613b",

        borderRadius:
            8,

        padding:
            "8px 12px",

        fontSize:
            12,

        fontWeight:
            700,

        cursor:
            "pointer",
    },


    deleteButton: {
        border:
            "1px solid #e2bcbc",

        background:
            "#fff4f4",

        color:
            "#963d3d",

        borderRadius:
            8,

        padding:
            "8px 12px",

        fontSize:
            12,

        fontWeight:
            700,

        cursor:
            "pointer",
    },


    emptyState: {
        textAlign:
            "center",

        padding:
            "60px 20px",

        color:
            "#7f847f",
    },


    emptyTitle: {
        color:
            "#18382c",

        fontWeight:
            800,

        fontSize:
            17,
    },


    emptyText: {
        fontSize:
            13,

        marginTop:
            6,
    },


    modalBackdrop: {
        position:
            "fixed",

        inset:
            0,

        background:
            "rgba(13, 29, 23, 0.48)",

        zIndex:
            9999,

        display:
            "flex",

        alignItems:
            "flex-start",

        justifyContent:
            "center",

        padding:
            "35px 18px",

        overflowY:
            "auto",

        boxSizing:
            "border-box",
    },


    modal: {
        width:
            "100%",

        maxWidth:
            820,

        background:
            "#ffffff",

        borderRadius:
            16,

        border:
            "1px solid #ddd8ce",

        boxShadow:
            "0 24px 70px rgba(0,0,0,0.22)",

        overflow:
            "hidden",
    },


    modalHeader: {
        padding:
            "21px 24px",

        display:
            "flex",

        alignItems:
            "flex-start",

        justifyContent:
            "space-between",

        gap:
            15,

        borderBottom:
            "1px solid #ece8e0",
    },


    modalTitle: {
        margin:
            0,

        fontSize:
            22,

        color:
            "#18382c",
    },


    modalSubtitle: {
        margin:
            "4px 0 0",

        color:
            "#858a85",

        fontSize:
            12,
    },


    closeButton: {
        appearance:
            "none",

        border:
            "none",

        background:
            "transparent",

        color:
            "#6e746f",

        fontSize:
            29,

        lineHeight:
            1,

        cursor:
            "pointer",

        padding:
            0,

        width:
            32,

        height:
            32,
    },


    modalBody: {
        padding:
            "22px 24px",
    },


    formSection: {
        marginBottom:
            27,
    },


    formSectionTitle: {
        margin:
            "0 0 13px",

        color:
            "#18382c",

        fontSize:
            15,

        fontWeight:
            800,

        paddingBottom:
            8,

        borderBottom:
            "1px solid #eeeae2",
    },


    twoColumnGrid: {
        display:
            "grid",

        gridTemplateColumns:
            "repeat(auto-fit, minmax(240px, 1fr))",

        gap:
            14,
    },


    field: {
        display:
            "flex",

        flexDirection:
            "column",

        gap:
            6,

        marginBottom:
            14,
    },


    label: {
        color:
            "#49554e",

        fontSize:
            12,

        fontWeight:
            700,
    },


    required: {
        color:
            "#a84242",

        marginLeft:
            3,
    },


    input: {
        width:
            "100%",

        boxSizing:
            "border-box",

        border:
            "1px solid #d9d5cc",

        borderRadius:
            9,

        background:
            "#ffffff",

        color:
            "#243d32",

        fontSize:
            14,

        padding:
            "10px 11px",

        outline:
            "none",

        minHeight:
            41,
    },


    textarea: {
        width:
            "100%",

        boxSizing:
            "border-box",

        border:
            "1px solid #d9d5cc",

        borderRadius:
            9,

        background:
            "#ffffff",

        color:
            "#243d32",

        fontSize:
            14,

        padding:
            "10px 11px",

        outline:
            "none",

        minHeight:
            85,

        resize:
            "vertical",

        fontFamily:
            "inherit",
    },


    helpText: {
        color:
            "#888d88",

        fontSize:
            11,

        lineHeight:
            1.5,

        marginTop:
            4,
    },


    artworkBox: {
        position:
            "relative",

        width:
            "100%",

        aspectRatio:
            "4 / 1",

        maxHeight:
            185,

        background:
            "#f1efe9",

        border:
            "1px dashed #cbc6bb",

        borderRadius:
            11,

        overflow:
            "hidden",
    },


    artworkPreview: {
        width:
            "100%",

        height:
            "100%",

        objectFit:
            "cover",

        display:
            "block",
    },


    artworkPlaceholder: {
        width:
            "100%",

        height:
            "100%",

        minHeight:
            130,

        display:
            "flex",

        alignItems:
            "center",

        justifyContent:
            "center",

        color:
            "#9a9d98",

        fontSize:
            13,
    },


    sponsoredPreview: {
        position:
            "absolute",

        right:
            9,

        bottom:
            9,

        color:
            "#ffffff",

        background:
            "rgba(18, 34, 28, 0.8)",

        padding:
            "4px 7px",

        borderRadius:
            5,

        fontSize:
            9,

        fontWeight:
            700,
    },


    artworkActions: {
        display:
            "flex",

        alignItems:
            "center",

        gap:
            12,

        flexWrap:
            "wrap",

        marginTop:
            11,
    },


    fileName: {
        color:
            "#69716c",

        fontSize:
            12,

        overflow:
            "hidden",

        textOverflow:
            "ellipsis",
    },


    toggleRow: {
        minHeight:
            41,

        display:
            "flex",

        alignItems:
            "center",

        gap:
            9,

        border:
            "1px solid #d9d5cc",

        borderRadius:
            9,

        padding:
            "0 11px",

        boxSizing:
            "border-box",

        color:
            "#35473e",

        fontSize:
            14,

        fontWeight:
            600,
    },


    checkbox: {
        width:
            17,

        height:
            17,

        accentColor:
            "#18382c",
    },


    progressBox: {
        background:
            "#f2f6f3",

        border:
            "1px solid #d6e2d9",

        color:
            "#315644",

        borderRadius:
            9,

        padding:
            "10px 12px",

        fontSize:
            12,

        fontWeight:
            600,
    },


    modalFooter: {
        padding:
            "16px 24px",

        borderTop:
            "1px solid #ece8e0",

        background:
            "#faf9f6",

        display:
            "flex",

        justifyContent:
            "flex-end",

        gap:
            10,
    },
};